import React, { useEffect, useState, useCallback } from 'react';
import { Clock, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * Non-invasive top banner that surfaces in the final 5 minutes before the
 * user's JWT expires. Lets the user save work and sign in again proactively
 * instead of being surprised by a 401 mid-action.
 *
 * Behaviour:
 *   - Only renders when the user is signed in AND `exp - now <= 5 min`.
 *   - Updates every second for a live countdown.
 *   - Yellow at >2 min remaining, orange at >1 min, red at <=1 min.
 *   - User can dismiss it; banner stays hidden until they sign in again.
 *   - Once the timer hits 0 the Tier A interceptor takes over.
 */

const FIVE_MIN_SECS = 5 * 60;

// Decode the payload segment of a JWT *without* verifying its signature —
// this is purely for surfacing the `exp` claim in the UI. Returns null on
// any failure (malformed token, etc.).
function decodeJwtExp(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // URL-safe base64 → standard base64
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - padded.length % 4) % 4;
    const json = atob(padded + '='.repeat(padLen));
    const payload = JSON.parse(decodeURIComponent(escape(json)));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

const formatRemaining = (secs) => {
  if (secs <= 0) return '0s';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const SessionExpiryBanner = () => {
  const { user, token } = useAuth();
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [dismissedExp, setDismissedExp] = useState(null);

  // Tick once a second. Cheap — single component, single setState per tick,
  // only schedules while we're authenticated.
  useEffect(() => {
    if (!user || !token) return undefined;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [user, token]);

  // If the user signs in again with a fresh token, clear the dismiss flag so
  // the next expiry window can surface its own banner.
  const exp = decodeJwtExp(token);
  useEffect(() => {
    if (exp && dismissedExp && exp !== dismissedExp) setDismissedExp(null);
  }, [exp, dismissedExp]);

  const handleDismiss = useCallback(() => {
    setDismissedExp(exp);
  }, [exp]);

  if (!user || !token || !exp) return null;
  if (dismissedExp === exp) return null;

  const remaining = exp - now;
  if (remaining > FIVE_MIN_SECS) return null;
  if (remaining <= 0) return null; // Tier A interceptor will fire on next 401

  let palette;
  if (remaining > 120) {
    palette = 'bg-amber-50 border-amber-300 text-amber-900';
  } else if (remaining > 60) {
    palette = 'bg-orange-50 border-orange-300 text-orange-900';
  } else {
    palette = 'bg-red-50 border-red-300 text-red-900';
  }

  return (
    <div
      role="alert"
      className={`fixed left-1/2 -translate-x-1/2 top-16 z-[60] max-w-xl w-[calc(100%-1rem)] border rounded-lg shadow-md flex items-center gap-3 px-4 py-2 text-sm ${palette}`}
      data-testid="session-expiry-banner"
    >
      <Clock className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1">
        Your session expires in <strong data-testid="session-expiry-countdown">{formatRemaining(remaining)}</strong>.
        Please save your work and sign in again.
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="p-1 rounded hover:bg-black/5 transition-colors"
        aria-label="Dismiss session expiry warning"
        data-testid="session-expiry-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default SessionExpiryBanner;
